import React, { useState, useEffect } from 'react';
import { X, Save, Settings, Plus, Trash2, Calendar, DollarSign, CheckCircle2 } from 'lucide-react';
import { financeiroService } from '../../services/financeiroService';
import { Categoria, FormaPagamento, TipoContaId } from '../../types/financeiro';
import { Medicoes, MedicoesAgregado } from '../../types/medicao';
import { supabase } from '../../lib/supabase';

export interface ParcelaItem {
  id: string;
  numero: number;
  totalParcelas: number;
  rotulo: string; // e.g. "1/3", "2/3"
  categoriaId: number;
  formaPagamentoId: number;
  dtVencimento: string;
  valor: number;
}

interface GerarFinanceiroModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  medicao: Medicoes | MedicoesAgregado | null;
  tipo: 'cliente' | 'agregado';
  clienteId?: number;
  clienteNome?: string;
  valorTotal: number;
}

const DEFAULT_CATEGORIAS_CLIENTE = [
  { id: 1, nome: 'Medição de Clientes', tipo_conta_id: 1 },
  { id: 2, nome: 'Prestação de Serviços de Transporte', tipo_conta_id: 1 },
  { id: 3, nome: 'Faturamento de Fretes', tipo_conta_id: 1 },
  { id: 4, nome: 'Locação de Veículos e Equipamentos', tipo_conta_id: 1 },
  { id: 5, nome: 'Outras Receitas Operacionais', tipo_conta_id: 1 },
];

const DEFAULT_CATEGORIAS_AGREGADO = [
  { id: 10, nome: 'Repasse de Agregados', tipo_conta_id: 2 },
  { id: 11, nome: 'Fretes Terceirizados', tipo_conta_id: 2 },
  { id: 12, nome: 'Serviços de Motoristas Agregados', tipo_conta_id: 2 },
  { id: 13, nome: 'Outras Despesas Operacionais', tipo_conta_id: 2 },
];

const DEFAULT_FORMAS_PAGAMENTO = [
  { id: 1, nome: 'Boleto Bancário' },
  { id: 2, nome: 'PIX' },
  { id: 3, nome: 'Transferência Bancária (TED/DOC)' },
  { id: 4, nome: 'Cartão de Crédito' },
  { id: 5, nome: 'Depósito em Conta' },
  { id: 6, nome: 'Cheque' },
  { id: 7, nome: 'Dinheiro' },
];

export function GerarFinanceiroModal({
  isOpen,
  onClose,
  onSuccess,
  medicao,
  tipo,
  clienteId,
  clienteNome,
  valorTotal,
}: GerarFinanceiroModalProps) {
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [loading, setLoading] = useState(false);

  // Form Controls
  const [categoriaGlobal, setCategoriaGlobal] = useState<number | ''>('');
  const [formaPagamentoGlobal, setFormaPagamentoGlobal] = useState<number | ''>('');
  const [primeiroVencimento, setPrimeiroVencimento] = useState<string>('');
  const [numParcelas, setNumParcelas] = useState<number>(1);
  const [parcelas, setParcelas] = useState<ParcelaItem[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, medicao]);

  const loadInitialData = async () => {
    try {
      const [cats, formas] = await Promise.all([
        financeiroService.getCategorias(),
        financeiroService.getFormasPagamento(),
      ]);

      const expectedTipoId = tipo === 'cliente' ? 1 : 2;
      let loadedCats = cats.filter(c => Number(c.tipo_conta_id) === expectedTipoId);

      if (loadedCats.length === 0) {
        loadedCats = (tipo === 'cliente' ? DEFAULT_CATEGORIAS_CLIENTE : DEFAULT_CATEGORIAS_AGREGADO) as Categoria[];
      }
      setCategorias(loadedCats);

      let loadedFormas = formas;
      if (loadedFormas.length === 0) {
        loadedFormas = DEFAULT_FORMAS_PAGAMENTO as FormaPagamento[];
      }
      setFormasPagamento(loadedFormas);

      // Default First Due Date (today + 15 days, or end of month)
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 15);
      const defaultDateStr = defaultDate.toISOString().split('T')[0];
      setPrimeiroVencimento(defaultDateStr);

      const defaultCatId = loadedCats[0]?.id || 1;
      const defaultFormaId = loadedFormas[0]?.id || 1;
      setCategoriaGlobal(defaultCatId);
      setFormaPagamentoGlobal(defaultFormaId);

      setNumParcelas(1);

      // Initialize 1 default installment
      const initialValor = valorTotal || 0;
      setParcelas([
        {
          id: 'p-1',
          numero: 1,
          totalParcelas: 1,
          rotulo: '1/1',
          categoriaId: defaultCatId,
          formaPagamentoId: defaultFormaId,
          dtVencimento: defaultDateStr,
          valor: Number(initialValor.toFixed(2)),
        },
      ]);
    } catch (err) {
      console.error('Error loading financeiro options:', err);
    }
  };

  if (!isOpen || !medicao) return null;

  const handleGerarParcelas = () => {
    const total = valorTotal || 0;
    const count = Math.max(1, Math.min(60, Number(numParcelas) || 1));
    const baseCat = Number(categoriaGlobal) || categorias[0]?.id || 1;
    const baseForma = Number(formaPagamentoGlobal) || formasPagamento[0]?.id || 1;
    const startDate = primeiroVencimento ? new Date(primeiroVencimento + 'T12:00:00') : new Date();

    const valorPorParcela = Math.floor((total / count) * 100) / 100;
    const sobraCentavos = Math.round((total - (valorPorParcela * count)) * 100) / 100;

    const novasParcelas: ParcelaItem[] = [];

    for (let i = 1; i <= count; i++) {
      const d = new Date(startDate);
      d.setMonth(d.getMonth() + (i - 1));
      const dtStr = d.toISOString().split('T')[0];

      // Add remaining cents to the last installment
      const val = i === count ? Number((valorPorParcela + sobraCentavos).toFixed(2)) : valorPorParcela;

      novasParcelas.push({
        id: `p-${i}-${Date.now()}`,
        numero: i,
        totalParcelas: count,
        rotulo: `${i}/${count}`,
        categoriaId: baseCat,
        formaPagamentoId: baseForma,
        dtVencimento: dtStr,
        valor: val,
      });
    }

    setParcelas(novasParcelas);
  };

  const handleAddParcela = () => {
    const nextNum = parcelas.length + 1;
    const lastDate = parcelas.length > 0 && parcelas[parcelas.length - 1].dtVencimento
      ? new Date(parcelas[parcelas.length - 1].dtVencimento + 'T12:00:00')
      : new Date();
    lastDate.setMonth(lastDate.getMonth() + 1);

    const baseCat = Number(categoriaGlobal) || categorias[0]?.id || 1;
    const baseForma = Number(formaPagamentoGlobal) || formasPagamento[0]?.id || 1;

    const nova: ParcelaItem = {
      id: `p-${nextNum}-${Date.now()}`,
      numero: nextNum,
      totalParcelas: nextNum,
      rotulo: `${nextNum}`,
      categoriaId: baseCat,
      formaPagamentoId: baseForma,
      dtVencimento: lastDate.toISOString().split('T')[0],
      valor: 0,
    };

    setParcelas([...parcelas, nova]);
  };

  const handleRemoveParcela = (id: string) => {
    if (parcelas.length <= 1) {
      alert('É necessário ter ao menos uma parcela.');
      return;
    }
    const filtered = parcelas.filter(p => p.id !== id);
    // Recalculate labels
    const reindexed = filtered.map((p, idx) => ({
      ...p,
      numero: idx + 1,
      totalParcelas: filtered.length,
      rotulo: `${idx + 1}/${filtered.length}`,
    }));
    setParcelas(reindexed);
  };

  const handleUpdateParcela = (id: string, field: keyof ParcelaItem, value: any) => {
    setParcelas(prev =>
      prev.map(p => {
        if (p.id === id) {
          return { ...p, [field]: value };
        }
        return p;
      })
    );
  };

  const handleSalvar = async () => {
    if (!medicao) return;
    if (medicao.status_medicao_id === 4) {
      alert('Esta medição já foi faturada e possui lançamentos financeiros gerados.');
      onClose();
      return;
    }
    if (parcelas.length === 0) {
      alert('Nenhuma parcela configurada.');
      return;
    }

    // Verify all parcelas have dates and positive values
    for (const p of parcelas) {
      if (!p.dtVencimento) {
        alert(`Por favor, preencha a data de vencimento da parcela ${p.rotulo}.`);
        return;
      }
      if (p.valor <= 0) {
        alert(`O valor da parcela ${p.rotulo} deve ser maior que zero.`);
        return;
      }
    }

    setLoading(true);
    try {
      const expectedTipoId = tipo === 'cliente' ? 1 : 2;
      const targetPessoaId = clienteId || 0;

      // Toda a geração (checagem de status + parcelas + atualização de status/OS)
      // roda em uma única transação atômica no banco (função gerar_financeiro_medicao),
      // com lock de linha na medição — evita faturamento duplicado por clique duplo
      // ou duas abas abertas simultaneamente.
      const payloadParcelas = parcelas.map((p) => ({
        tipo_conta_id: expectedTipoId,
        categoria_id: p.categoriaId || null,
        forma_pagamento_id: p.formaPagamentoId || null,
        descricao: tipo === 'cliente'
          ? `Medição #${medicao.id} - Parcela ${p.rotulo} - ${clienteNome || 'Cliente'}`
          : `Repasse Medição #${medicao.id} - Parcela ${p.rotulo} - ${clienteNome || 'Agregado'}`,
        valor: p.valor,
        dt_vencimento: p.dtVencimento,
        numero: p.numero,
        obs: `Gerado automaticamente pela Medição #${medicao.id} (${tipo === 'cliente' ? 'Cliente' : 'Agregado'})`,
      }));

      const { error } = await supabase.rpc('gerar_financeiro_medicao', {
        p_medicao_id: medicao.id,
        p_tipo: tipo,
        p_pessoa_id: targetPessoaId,
        p_parcelas: payloadParcelas,
      });

      if (error) {
        if (error.message?.includes('MEDICAO_JA_FATURADA')) {
          alert('Esta medição já foi faturada (por você ou outra pessoa) — nenhum lançamento duplicado foi criado.');
          onClose();
          return;
        }
        throw error;
      }

      onSuccess(
        `Lançamento financeiro gerado com sucesso para a Medição #${medicao.id} (${parcelas.length} ${
          parcelas.length === 1 ? 'parcela' : 'parcelas'
        })!`
      );
      onClose();
    } catch (err: any) {
      console.error('Erro ao gerar contas financeiras:', err);
      alert('Erro ao salvar parcelas no módulo financeiro: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const valorFormatado = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valorTotal || 0);

  const somaParcelas = parcelas.reduce((acc, p) => acc + (Number(p.valor) || 0), 0);
  const diferenca = Math.abs(somaParcelas - (valorTotal || 0));

  return (
    <div
      id="modal-gerar-financeiro"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-4 animate-in fade-in duration-150 overflow-y-auto"
    >
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col my-auto max-h-[92vh]">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-800 tracking-tight">Gerar Financeiro</h2>
            {tipo === 'cliente' ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                Conta a Receber
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
                Conta a Pagar
              </span>
            )}
          </div>
          <button
            id="btn-close-gerar-financeiro"
            onClick={onClose}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors shadow-xs cursor-pointer"
          >
            <X className="h-4 w-4 text-slate-500" />
            <span>Fechar</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          
          {/* Top Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            
            {/* Nº Medição */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Nº Medição:
              </label>
              <input
                type="text"
                disabled
                value={medicao.id}
                className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 shadow-xs cursor-not-allowed"
              />
            </div>

            {/* Cliente / Agregado */}
            <div className="md:col-span-9">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {tipo === 'cliente' ? 'Cliente:' : 'Agregado:'}
              </label>
              <input
                type="text"
                disabled
                value={clienteNome || '-'}
                className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-bold text-slate-800 shadow-xs cursor-not-allowed truncate"
                title={clienteNome}
              />
            </div>

            {/* Valor Medição */}
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Valor Medição:
              </label>
              <input
                type="text"
                disabled
                value={valorFormatado}
                className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-extrabold text-slate-800 shadow-xs cursor-not-allowed"
              />
            </div>

            {/* Categoria de Conta */}
            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Categoria de Conta:
              </label>
              <select
                value={categoriaGlobal}
                onChange={(e) => setCategoriaGlobal(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Selecionar</option>
                {categorias.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Forma de Pagamento */}
            <div className="md:col-span-5">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Forma de Pagamento:
              </label>
              <select
                value={formaPagamentoGlobal}
                onChange={(e) => setFormaPagamentoGlobal(e.target.value ? Number(e.target.value) : '')}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Selecionar</option>
                {formasPagamento.map(forma => (
                  <option key={forma.id} value={forma.id}>
                    {forma.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Primeiro Vencimento */}
            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Primeiro Vencimento:
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={primeiroVencimento}
                  onChange={(e) => setPrimeiroVencimento(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Parcelas */}
            <div className="md:col-span-4">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Parcelas:
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={numParcelas}
                onChange={(e) => setNumParcelas(Math.max(1, Number(e.target.value) || 1))}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Botão Gerar Parcelas */}
            <div className="md:col-span-4 flex items-end">
              <button
                type="button"
                id="btn-gerar-parcelas"
                onClick={handleGerarParcelas}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white px-4 py-2 text-sm font-bold shadow-xs transition-colors cursor-pointer"
              >
                <Settings className="h-4 w-4" />
                <span>Gerar Parcelas</span>
              </button>
            </div>

          </div>

          {/* Installments Table / Section */}
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
            <div className="bg-slate-100 px-4 py-2.5 border-b border-slate-200 flex justify-between items-center">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Grade de Parcelas ({parcelas.length})
              </span>
              <div className="text-xs font-semibold">
                <span className="text-slate-500">Soma das parcelas: </span>
                <span className={`font-bold ${diferenca > 0.05 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(somaParcelas)}
                </span>
                {diferenca > 0.05 && (
                  <span className="text-amber-600 ml-2 text-2xs font-normal">
                    (Diferença de R$ {diferenca.toFixed(2)})
                  </span>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                    <th className="py-2.5 px-3 w-16 text-center">Parcela</th>
                    <th className="py-2.5 px-3 min-w-[150px]">Categoria</th>
                    <th className="py-2.5 px-3 min-w-[150px]">Forma Pagamento</th>
                    <th className="py-2.5 px-3 min-w-[140px]">Data de vencimento</th>
                    <th className="py-2.5 px-3 min-w-[120px]">Valor (R$)</th>
                    <th className="py-2.5 px-3 w-12 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {parcelas.map((p, index) => (
                    <tr key={p.id} className="hover:bg-slate-50/70 transition-colors">
                      
                      {/* Parcela Label */}
                      <td className="py-2 px-3 text-center">
                        <span className="inline-block bg-slate-100 text-slate-700 font-bold px-2 py-1 rounded text-xs border border-slate-200">
                          {p.rotulo || `${index + 1}`}
                        </span>
                      </td>

                      {/* Categoria */}
                      <td className="py-2 px-3">
                        <select
                          value={p.categoriaId}
                          onChange={(e) => handleUpdateParcela(p.id, 'categoriaId', Number(e.target.value))}
                          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Selecionar</option>
                          {categorias.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.nome}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Forma Pagamento */}
                      <td className="py-2 px-3">
                        <select
                          value={p.formaPagamentoId}
                          onChange={(e) => handleUpdateParcela(p.id, 'formaPagamentoId', Number(e.target.value))}
                          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Selecionar</option>
                          {formasPagamento.map(forma => (
                            <option key={forma.id} value={forma.id}>
                              {forma.nome}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Data de Vencimento */}
                      <td className="py-2 px-3">
                        <input
                          type="date"
                          value={p.dtVencimento}
                          onChange={(e) => handleUpdateParcela(p.id, 'dtVencimento', e.target.value)}
                          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800 font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </td>

                      {/* Valor */}
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={p.valor}
                          onChange={(e) => handleUpdateParcela(p.id, 'valor', Number(e.target.value))}
                          className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs font-bold text-slate-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                      </td>

                      {/* Remover Linha */}
                      <td className="py-2 px-3 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveParcela(p.id)}
                          className="p-1 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors cursor-pointer"
                          title="Remover parcela"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Adicionar Parcela */}
            <div className="p-2 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={handleAddParcela}
                className="flex items-center gap-1 px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg transition-colors cursor-pointer"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Adicionar Parcela</span>
              </button>
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 flex items-center justify-between">
          <button
            type="button"
            id="btn-salvar-financeiro"
            onClick={handleSalvar}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 py-2 text-sm font-bold shadow-md transition-all disabled:opacity-50 cursor-pointer"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Salvando...' : 'Salvar'}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
